/**
 * Tracks "submit in flight" across page reloads.
 *
 * When the user clicks Submit, the .NET backend does a 5-15s cross-object SF write
 * that is best-effort to complete even if the client tab is refreshed or closed. If
 * the tab is refreshed mid-submit, the next form-load can hit SF while Status is
 * still 'In Progress' — making the form look editable when it's actually moments
 * away from being locked. The user can land on the review page, edit, and re-submit
 * against a record that's already Submitted (UpdateDraftAsync silently no-ops; the
 * edits are lost). This module gives FormPage a signal to detect that scenario on
 * the post-refresh load and poll until SF resolves the status.
 *
 * Keyed by formId so multiple in-flight submits across tabs/forms stay independent.
 * Uses localStorage (not sessionStorage) so closing-and-reopening the tab in the
 * same browser preserves the in-flight signal.
 */

const KEY_PREFIX = "submitInFlight:";
/** Submit endpoint timeout is 60s; allow a small buffer for SF tail-latency. */
const MAX_WAIT_MS = 90_000;
export const POLL_INTERVAL_MS = 2_000;

export function markSubmitInFlight(formId: string): void {
  localStorage.setItem(`${KEY_PREFIX}${formId}`, String(Date.now()));
}

export function clearSubmitInFlight(formId: string): void {
  localStorage.removeItem(`${KEY_PREFIX}${formId}`);
}

/**
 * Returns the timestamp (ms epoch) when a submit was last marked in-flight for this
 * form, or null when no fresh signal exists. Stale entries (older than MAX_WAIT_MS)
 * are cleaned up and treated as absent — a crashed tab from yesterday should not
 * gate a fresh page load.
 */
export function getSubmitInFlight(formId: string): number | null {
  const raw = localStorage.getItem(`${KEY_PREFIX}${formId}`);
  if (!raw) return null;
  const startedAt = Number(raw);
  if (!Number.isFinite(startedAt) || Date.now() - startedAt > MAX_WAIT_MS) {
    clearSubmitInFlight(formId);
    return null;
  }
  return startedAt;
}

export function getSubmitDeadlineMs(startedAt: number): number {
  return startedAt + MAX_WAIT_MS;
}
